import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatCurrency, formatDate, CURRENCY_LIST } from '@/lib/api';

const EMPTY_FORM = {
  type: 'payable', reference_id: '', reference_number: '', amount: 0,
  currency: 'USD', payment_method: 'bank_transfer', payment_date: '', notes: ''
};

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'wire', label: 'Wire Transfer' },
];

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const fetchPayments = useCallback(async () => {
    try { const { data } = await api.get('/payments'); setPayments(data); } catch { toast.error('Failed to load payments'); }
    setLoading(false);
  }, []);

  const fetchInvoices = useCallback(async (type) => {
    try {
      if (type === 'payable') {
        const { data } = await api.get('/invoices/payable');
        setInvoices(data.filter(i => i.status !== 'paid'));
      } else {
        const { data } = await api.get('/invoices/receivable');
        setInvoices(data.filter(i => i.status !== 'paid'));
      }
    } catch { setInvoices([]); }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const openDialog = () => {
    setForm({ ...EMPTY_FORM });
    fetchInvoices('payable');
    setDialogOpen(true);
  };

  const handleTypeChange = (type) => {
    setForm({ ...form, type, reference_id: '', reference_number: '' });
    fetchInvoices(type);
  };

  const handleInvoiceSelect = (invId) => {
    const inv = invoices.find(i => i.id === invId);
    if (inv) {
      const num = inv.invoice_number || inv.po_number || '';
      setForm({ ...form, reference_id: invId, reference_number: num, amount: inv.amount, currency: inv.currency });
    }
  };

  const handleSave = async () => {
    if (!form.amount) { toast.error('Amount is required'); return; }
    setSaving(true);
    try {
      await api.post('/payments', form);
      toast.success('Payment recorded');
      setDialogOpen(false);
      fetchPayments();
    } catch { toast.error('Failed to record payment'); }
    setSaving(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Track and record payment transactions</p>
        </div>
        <Button onClick={openDialog} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="record-payment-button">
          <Plus size={16} /> Record Payment
        </Button>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Type</TableHead>
                <TableHead className="text-muted-foreground">Reference</TableHead>
                <TableHead className="text-muted-foreground">Amount</TableHead>
                <TableHead className="text-muted-foreground">Method</TableHead>
                <TableHead className="text-muted-foreground">Date</TableHead>
                <TableHead className="text-muted-foreground">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8"><Loader2 className="animate-spin inline" size={16} /> Loading...</TableCell></TableRow>
              ) : payments.length === 0 ? (
                <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payments recorded yet.</TableCell></TableRow>
              ) : payments.map(p => (
                <TableRow key={p.id} className="border-border hover:bg-muted/50" data-testid={`payment-row-${p.id}`}>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs capitalize ${p.type === 'payable' ? 'status-pending' : 'status-sent'}`}>{p.type === 'payable' ? 'Outgoing' : 'Incoming'}</Badge>
                  </TableCell>
                  <TableCell className="text-card-foreground/80 font-mono text-xs">{p.reference_number || '-'}</TableCell>
                  <TableCell className="text-foreground font-mono">{formatCurrency(p.amount, p.currency)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm capitalize">{(p.payment_method || '').replace('_', ' ')}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(p.payment_date || p.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{p.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground">Record Payment</DialogTitle>
            <DialogDescription className="text-muted-foreground">Log a payment transaction</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Payment Type</Label>
                <Select value={form.type} onValueChange={handleTypeChange}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="payable" className="text-foreground">Outgoing (Payable)</SelectItem>
                    <SelectItem value="receivable" className="text-foreground">Incoming (Receivable)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Link to Invoice</Label>
                <Select value={form.reference_id} onValueChange={handleInvoiceSelect}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {invoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id} className="text-foreground">
                        {inv.invoice_number || inv.po_number} - {formatCurrency(inv.amount, inv.currency)}
                      </SelectItem>
                    ))}
                    {invoices.length === 0 && <SelectItem value="none" disabled className="text-muted-foreground">No unpaid invoices</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Amount *</Label>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: Number(e.target.value) })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-amount-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {CURRENCY_LIST.map(c => <SelectItem key={c} value={c} className="text-foreground">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Payment Method</Label>
                <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value} className="text-foreground">{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-card-foreground/80">Payment Date</Label>
                <Input type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })}
                  className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-date-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-card-foreground/80">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="payment-notes-input" placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-input text-card-foreground/80">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-payment-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
